import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchCandidateAnalysisDetailV2 } from '../../api/v2/candidates';
import type { CandidateAnalysisDetailV2, CriterionRulingV2 } from '../../types';
import NotFound from '../../components/NotFound';

/**
 * v2 Candidate Analysis (page 2). Per-criterion judgement view for one
 * candidate against one tender: requirement-by-requirement and
 * responsibility-by-responsibility, each row a claim -> judgement -> evidence.
 *
 * Each row carries the two-axis finding verdict: the strength rung
 * (STRONG / SOLID / THIN / UNSUPPORTED / NEEDS_CLARIFYING) shown as the primary
 * badge, and the orthogonal anchoring axis (Explicit / Implicit) shown as a
 * secondary badge when present. Both are mapped from the raw finding values
 * here on the frontend; the must-have dismissal gate (§5 judgment layer) is
 * not built yet, so the header summarises the strength mix instead.
 */

const TIER_LABEL: Record<CriterionRulingV2['tier'], string> = {
  MUST_HAVE: 'Must have',
  STATED: 'Stated',
  NICE_TO_HAVE: 'Nice to have',
};

/** Map a raw strength verdict (or null = no finding yet) to row label + colour. */
function verdictDisplay(verdict: CriterionRulingV2['verdict']): { label: string; cls: string } {
  switch (verdict) {
    case 'STRONG':
      return { label: 'Strong', cls: 'bg-green-50 text-green-700 border-green-200' };
    case 'SOLID':
      return { label: 'Solid', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    case 'THIN':
      return { label: 'Thin', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    case 'UNSUPPORTED':
      return { label: 'Unsupported', cls: 'bg-rose-50 text-rose-700 border-rose-200' };
    case 'NEEDS_CLARIFYING':
      return { label: 'Needs clarifying', cls: 'bg-gray-100 text-gray-600 border-gray-200' };
    default:
      return { label: 'Needs analysis', cls: 'bg-gray-50 text-gray-400 border-gray-300 border-dashed' };
  }
}

/** Map the anchoring axis to a subtle secondary badge label. */
function anchoringLabel(anchoring: CriterionRulingV2['anchoring']): string | null {
  switch (anchoring) {
    case 'EXPLICIT':
      return 'Explicit';
    case 'IMPLICIT':
      return 'Implicit';
    default:
      return null;
  }
}

function CriterionRow({ row }: { row: CriterionRulingV2 }) {
  const verdict = verdictDisplay(row.verdict);
  const anchoring = anchoringLabel(row.anchoring);
  return (
    <li className="border border-gray-200 rounded-xl bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
            {TIER_LABEL[row.tier]}
          </span>
          <p className="text-sm font-medium text-gray-900 mt-0.5">{row.title}</p>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          {anchoring && (
            <span className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500">
              {anchoring}
            </span>
          )}
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${verdict.cls}`}
          >
            {verdict.label}
          </span>
        </div>
      </div>

      {row.rationale && <p className="text-sm text-gray-600 mt-3">{row.rationale}</p>}

      {row.citations.length > 0 && (
        <ul className="mt-3 space-y-2">
          {row.citations.map((quote, i) => (
            <li
              key={i}
              className="border-l-2 border-gray-200 pl-3 text-sm text-gray-500 italic"
            >
              &ldquo;{quote}&rdquo;
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function CriterionGroup({ title, rows }: { title: string; rows: CriterionRulingV2[] }) {
  if (rows.length === 0) return null;
  return (
    <section>
      <h2 className="text-sm font-semibold text-gray-900 mb-3">
        {title} <span className="text-gray-400 font-normal">· {rows.length}</span>
      </h2>
      <ul className="space-y-3">
        {rows.map((row) => (
          <CriterionRow key={row.criterion_id} row={row} />
        ))}
      </ul>
    </section>
  );
}

function SummaryChip({ n, label, cls }: { n: number; label: string; cls: string }) {
  return (
    <li>
      <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${cls}`}>
        {n} {label}
      </span>
    </li>
  );
}

export default function CandidateAnalysis() {
  const { tenderId, candidateId } = useParams<{ tenderId: string; candidateId?: string }>();
  const [detail, setDetail] = useState<CandidateAnalysisDetailV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!tenderId || !candidateId) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetchCandidateAnalysisDetailV2(tenderId, candidateId)
      .then((data) => {
        if (cancelled) return;
        if (data === null) setNotFound(true);
        else setDetail(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenderId, candidateId]);

  if (!loading && notFound) return <NotFound />;

  const requirements = detail?.criteria.filter((c) => c.kind === 'REQUIREMENT') ?? [];
  const responsibilities = detail?.criteria.filter((c) => c.kind === 'RESPONSIBILITY') ?? [];

  // Strength tally for the header, derived from the rows.
  const counts = (detail?.criteria ?? []).reduce(
    (acc, c) => {
      if (c.verdict === 'STRONG') acc.strong += 1;
      else if (c.verdict === 'SOLID') acc.solid += 1;
      else if (c.verdict === 'THIN') acc.thin += 1;
      else if (c.verdict === 'UNSUPPORTED') acc.unsupported += 1;
      else if (c.verdict === 'NEEDS_CLARIFYING') acc.needsClarifying += 1;
      else acc.pending += 1;
      return acc;
    },
    { strong: 0, solid: 0, thin: 0, unsupported: 0, needsClarifying: 0, pending: 0 },
  );

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="text-sm text-gray-500">
        <ol className="flex items-center gap-2">
          <li>
            <Link to="/v2/tenders" className="hover:text-brand-700">
              Tenders
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li>
            <Link to={`/v2/tenders/${tenderId}`} className="hover:text-brand-700">
              Overview
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li className="text-gray-800 font-medium truncate max-w-xs">
            {detail?.candidate_name || 'Candidate'}
          </li>
        </ol>
      </nav>

      {loading ? (
        <div className="text-center py-16 text-gray-400 text-sm">Loading analysis…</div>
      ) : detail ? (
        <>
          {/* Candidate header */}
          <section className="bg-white rounded-xl border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900">
              {detail.candidate_name || 'Candidate'}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Analyzed {detail.ruled}/{detail.total} criteria
            </p>
            <ul className="mt-4 flex flex-wrap gap-1.5">
              {counts.strong > 0 && (
                <SummaryChip n={counts.strong} label="strong" cls="bg-green-50 text-green-700 border-green-200" />
              )}
              {counts.solid > 0 && (
                <SummaryChip n={counts.solid} label="solid" cls="bg-emerald-50 text-emerald-700 border-emerald-200" />
              )}
              {counts.thin > 0 && (
                <SummaryChip n={counts.thin} label="thin" cls="bg-amber-50 text-amber-700 border-amber-200" />
              )}
              {counts.unsupported > 0 && (
                <SummaryChip n={counts.unsupported} label="unsupported" cls="bg-rose-50 text-rose-700 border-rose-200" />
              )}
              {counts.needsClarifying > 0 && (
                <SummaryChip n={counts.needsClarifying} label="needs clarifying" cls="bg-gray-100 text-gray-600 border-gray-200" />
              )}
              {counts.pending > 0 && (
                <SummaryChip n={counts.pending} label="needs analysis" cls="bg-gray-50 text-gray-400 border-gray-300" />
              )}
            </ul>
          </section>

          {detail.total === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">
              No requirements or responsibilities were extracted for this tender.
            </div>
          ) : (
            <div className="space-y-6">
              <CriterionGroup title="Requirements" rows={requirements} />
              <CriterionGroup title="Responsibilities" rows={responsibilities} />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
